import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getOnboardingInfo, startOnboarding } from "../api/onboarding";
import type {
  OnboardingStep,
  StartOnboardingDto,
  StartOnboardingRequest,
} from "../types/onboarding-types";

export function useOrganizationOnboarding() {
  const [step, setStep] = useState<OnboardingStep>("intro");
  const [error, setError] = useState<string | null>(null);

  const { data: onboardingInfo, isLoading } = useQuery({
    queryKey: ["onboarding-info"],
    queryFn: getOnboardingInfo,
  });

  const orgCandidates = useMemo(() => onboardingInfo?.org_candidates ?? [], [onboardingInfo]);

  const [form, setForm] = useState<StartOnboardingDto>({
    new_org_name: "",
  });

  useEffect(() => {
    if (orgCandidates.length > 0 && !form.new_org_name) {
      setForm((prev: StartOnboardingDto) => ({
        ...prev,
        new_org_name: orgCandidates[0].eng_identifier,
      }));
    }
  }, [orgCandidates, form.new_org_name]);

  const onboardingMutation = useMutation({
    mutationFn: (request: StartOnboardingRequest) => startOnboarding(request),
    onSuccess: () => {
      setError(null);
      setStep("done");
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "온보딩 중 오류가 발생했습니다.");
      setStep("done");
    },
  });

  const start = async () => {
    if (!form.new_org_name) return { ok: false as const, error: "조직을 선택해 주세요." };

    setStep("onboarding");
    setError(null);

    const request: StartOnboardingRequest = {
      new_org_name: form.new_org_name,
    };

    try {
      await onboardingMutation.mutateAsync(request);
      return { ok: true as const };
    } catch {
      return { ok: false as const, error: "온보딩 중 오류가 발생했습니다." };
    }
  };

  const finish = () => {
    setError(null);
    setStep("intro");
  };

  return {
    step,
    setStep,
    error,
    orgCandidates,
    form,
    setForm,
    start,
    finish,
    isLoading,
    isProcessing: onboardingMutation.isPending,
  };
}
